import { useState, useCallback } from "react";
import type { ApiBid } from "../mappers/types";

const generateId = () => Math.floor(Math.random() * 1000000000) + 10000000;

/** Default numeric fields required by the backend LineItemBase schema. */
const DEFAULT_LINE_ITEM_FIELDS = {
    vendor_rate: 0,
    markup: 0,
    markup_amount: 0,
    adjustment: 0,
    ot_1_5: 0,
    ot_2_0: 0,
    budget_buffer: 0,
    budget_buffer_amount: 0,
    exchange_rate_buffer: 0,
    quantity: 0,
    days: 1,
    internal_rate: null,
    internal_markup: null,
    internal_notes: "",
    external_notes: "",
};

/**
 * Client-only bid state management for the Atomic Bid Creator.
 *
 * Mirrors the API shape of `useBidAPI` but operates entirely in-memory —
 * no network requests until the user submits the final atomic payload.
 *
 * Action strings follow the format: "ACTION_TYPE:targetId:optionalName"
 */
export function useLocalBidState(initialState: ApiBid | null = null) {
    const [bid, setBid] = useState<ApiBid | null>(initialState);

    const reorderNodes = useCallback(
        async (
            sourceIdMap: { parentId: number | null; ids: number[] },
            targetIdMap?: { parentId: number | null; ids: number[] },
        ) => {
            setBid((prev) => {
                if (!prev) return null;
                const clone = structuredClone(prev);
                const groups = clone.line_groups ?? [];
                const items = (clone as any).line_items ?? [];

                // Update sort_order for source siblings
                sourceIdMap.ids.forEach((id, idx) => {
                    const group = groups.find((g: any) => g.id === id);
                    if (group) {
                        group.sort_order = idx;
                        return;
                    }
                    const item = items.find((i: any) => i.id === id);
                    if (item) item.sort_order = idx;
                });

                // Update sort_order + parent for target siblings (cross-parent move)
                if (targetIdMap) {
                    targetIdMap.ids.forEach((id, idx) => {
                        const group = groups.find((g: any) => g.id === id);
                        if (group) {
                            group.sort_order = idx;
                            group.parent_id = targetIdMap.parentId;
                            return;
                        }
                        const item = items.find((i: any) => i.id === id);
                        if (item) {
                            item.sort_order = idx;
                            item.line_group_id = targetIdMap.parentId;
                        }
                    });
                }

                return clone;
            });
        },
        [],
    );

    const updateField = useCallback(
        async (
            modelType: "line_item" | "line_group",
            modelId: number,
            field: string,
            val: any,
        ) => {
            setBid((prev) => {
                if (!prev) return null;
                const clone = structuredClone(prev);

                if (modelType === "line_group") {
                    const group = clone.line_groups?.find(
                        (g: any) => g.id === modelId,
                    );
                    if (group) (group as any)[field] = val;
                } else {
                    const item = (clone as any).line_items?.find(
                        (i: any) => i.id === modelId,
                    );
                    if (item) item[field] = val;
                }

                return clone;
            });
        },
        [],
    );

    const addAction = useCallback(async (action: string) => {
        const parts = action.split(":");
        const actionType = parts[0];
        const targetId = parts[1] ? parseInt(parts[1]) : NaN;
        const itemName = parts[2] || "";

        setBid((prev) => {
            if (!prev) return null;
            const clone = structuredClone(prev);

            switch (actionType) {
                case "ADD_ROOT_GROUP": {
                    const name = itemName || "New Root section";
                    clone.line_groups = clone.line_groups ?? [];
                    clone.line_groups.push({
                        id: generateId(),
                        name,
                        parent_id: null,
                        rate_type: "Days",
                        is_internal: false,
                        markup: 0,
                        sort_order: clone.line_groups.filter(
                            (g) => !g.parent_id,
                        ).length,
                    } as any);
                    break;
                }
                case "ADD_SUBGROUP": {
                    const name = itemName || "New Category";
                    clone.line_groups = clone.line_groups ?? [];
                    clone.line_groups.push({
                        id: generateId(),
                        parent_id: targetId,
                        name,
                        rate_type: "Days",
                        is_internal: false,
                        sort_order: clone.line_groups.filter(
                            (g) => g.parent_id === targetId,
                        ).length,
                    } as any);
                    break;
                }
                case "ADD_ITEM": {
                    const name = itemName || "New Line Item";
                    const existingItems: any[] =
                        (clone as any).line_items ?? [];
                    existingItems.push({
                        id: generateId(),
                        line_group_id: targetId,
                        name,
                        vendor_currency: "USD",
                        rate_type: "Days",
                        is_internal: false,
                        sort_order: existingItems.filter(
                            (i: any) => i.line_group_id === targetId,
                        ).length,
                        ...DEFAULT_LINE_ITEM_FIELDS,
                    });
                    (clone as any).line_items = existingItems;
                    break;
                }
                case "DELETE_GROUP": {
                    const cascadeIds = new Set<number>([targetId]);
                    clone.line_groups
                        ?.filter((g) => g.parent_id === targetId)
                        .forEach((child) => cascadeIds.add(child.id));

                    clone.line_groups = clone.line_groups?.filter(
                        (g) => !cascadeIds.has(g.id),
                    );
                    (clone as any).line_items = (
                        clone as any
                    ).line_items?.filter(
                        (i: any) => !cascadeIds.has(i.line_group_id),
                    );
                    break;
                }
                case "DELETE_ITEM": {
                    (clone as any).line_items = (
                        clone as any
                    ).line_items?.filter((i: any) => i.id !== targetId);
                    break;
                }
                case "DELETE_ADDON": {
                    clone.addons = clone.addons?.filter(
                        (a) => a.id !== targetId,
                    );
                    break;
                }
                case "DUPLICATE_ITEM": {
                    const items: any[] = (clone as any).line_items ?? [];
                    const source = items.find((i: any) => i.id === targetId);
                    if (source) {
                        const maxSort = items
                            .filter(
                                (i: any) =>
                                    i.line_group_id === source.line_group_id,
                            )
                            .reduce(
                                (max: number, i: any) =>
                                    Math.max(max, i.sort_order ?? 0),
                                0,
                            );
                        items.push({
                            ...source,
                            id: generateId(),
                            name: `${source.name} (copy)`,
                            sort_order: maxSort + 1,
                        });
                    }
                    break;
                }
                case "DUPLICATE_GROUP": {
                    const groups = clone.line_groups ?? [];
                    const items: any[] = (clone as any).line_items ?? [];
                    const source = groups.find((g: any) => g.id === targetId);
                    if (source) {
                        const newGroupId = generateId();
                        const maxSort = groups
                            .filter(
                                (g: any) => g.parent_id === source.parent_id,
                            )
                            .reduce(
                                (max: number, g: any) =>
                                    Math.max(max, g.sort_order ?? 0),
                                0,
                            );

                        // Clone the group
                        groups.push({
                            ...source,
                            id: newGroupId,
                            name: `${source.name} (copy)`,
                            sort_order: maxSort + 1,
                        } as any);

                        // Clone child groups
                        const childGroups = groups.filter(
                            (g: any) => g.parent_id === targetId,
                        );
                        childGroups.forEach((child: any) => {
                            const newChildId = generateId();
                            groups.push({
                                ...child,
                                id: newChildId,
                                parent_id: newGroupId,
                            });

                            // Clone child items
                            items
                                .filter(
                                    (i: any) => i.line_group_id === child.id,
                                )
                                .forEach((item: any) => {
                                    items.push({
                                        ...item,
                                        id: generateId(),
                                        line_group_id: newChildId,
                                    });
                                });
                        });

                        // Clone direct items
                        items
                            .filter((i: any) => i.line_group_id === targetId)
                            .forEach((item: any) => {
                                items.push({
                                    ...item,
                                    id: generateId(),
                                    line_group_id: newGroupId,
                                });
                            });
                    }
                    break;
                }
                default:
                    console.warn(
                        `useLocalBidState: Unhandled action "${actionType}"`,
                    );
            }

            return clone;
        });
    }, []);

    return {
        bid,
        setBid,
        loading: false,
        fetchBid: useCallback(async () => {}, []),
        reorderNodes,
        updateField,
        addAction,
        cleanBid: useCallback(async () => {}, []),
        zeroOutRunning: useCallback(async () => {}, []),
    };
}
